import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { start, stop } from '../../../actions/stream';
import { fetchSymbolIfNeeded } from '../../../actions/symbol';
import { createChannelSelector } from '../../../selectors/channelSelectors';
import useActions from '../../../utils/hooks/useActions';
import useSymbolMetadata from '../../../utils/hooks/useSymbolMetadata';
import {
  calcNetChg,
  calcNetChgPct,
  calcPriceMarkerPosition
} from '../../../utils/maths';
import { getSymbolClose } from '../../../utils/symbol';
import CssFlash from '../../common/CssFlash';
import DateComponent from '../../common/DateComponent';
import Number from '../../common/Number';
import Time from '../../common/Time';
import './SymbolQuoteCard.scss';

/**
 * SymbolQuoteCard Component - Displays Open, High, Low, Close data for a financial symbol
 *
 * @param {Object} props Component props
 * @param {Object} props.symbol Symbol identifier
 * @returns {React.Component} Symbol Quote Card display component
 */
const SymbolQuoteCard = ({ symbol }) => {
  const [startStream] = useActions([start], []);
  const [stopStream] = useActions([stop], []);
  const [getSymbolMetadata] = useActions([fetchSymbolIfNeeded], []);
  const metadata = useSymbolMetadata(symbol);
  const channelName = `${symbol}/quote`;
  const quote = useSelector(createChannelSelector(channelName)) || {};

  useEffect(() => {
    getSymbolMetadata(symbol);
    startStream(channelName);
    return () => {
      stopStream(channelName);
    };
  }, [channelName, getSymbolMetadata, startStream, stopStream, symbol]);

  // Format numbers with commas

  const close = getSymbolClose(metadata.assetClass, quote);
  const netChange = calcNetChg(close, quote.prevClose);
  const netChangePct = calcNetChgPct(close, quote.prevClose);

  return (
    <div className="symbol-quote-card-container">
      <table className="symbol-quote-card-table">
        <tbody>
          <tr>
            <th>現在値</th>
            <td>
              <CssFlash value={close} isChange={false}>
                <Number
                  value={close}
                  precision={metadata.precision}
                  hideIfZero={true}
                  className="close-price"
                />
              </CssFlash>
            </td>
          </tr>
          <tr>
            <th>前日比</th>
            <td>
              <CssFlash value={netChange} isChange={false}>
                <Number
                  value={netChange}
                  precision={metadata.precision}
                  signed={true}
                  className="net-change"
                />
                <span>(</span>
                <Number
                  value={netChangePct}
                  precision={2}
                  signed={true}
                  suffix={'%'}
                  className="net-change-pct"
                />
                <span>)</span>
              </CssFlash>
            </td>
          </tr>
          <tr className="divider">
            <td></td>
            <td>
              <Time value={quote.lastTime} fallback={<></>} />
            </td>
          </tr>
          <tr className="divider">
            <th>始値</th>
            <td>
              <CssFlash value={quote.open} isChange={false}>
                <Number
                  value={quote.open}
                  precision={metadata.precision}
                  className="price-value"
                />
              </CssFlash>
              <Time value={quote.openTime} fallback={<></>} />
            </td>
          </tr>
          <tr className="divider">
            <th>高値</th>
            <td>
              <CssFlash value={quote.high} isChange={false}>
                <Number
                  value={quote.high}
                  precision={metadata.precision}
                  className="price-value"
                />
              </CssFlash>
              <Time value={quote.highTime} fallback={<></>} />
            </td>
          </tr>
          <tr className="divider">
            <th>安値</th>
            <td>
              <CssFlash value={quote.low} isChange={false}>
                <Number
                  value={quote.low}
                  precision={metadata.precision}
                  className="price-value"
                />
              </CssFlash>
              <Time value={quote.lowTime} fallback={<></>} />
            </td>
          </tr>
          <tr className="divider">
            <th>前日終値</th>
            <td>
              <CssFlash value={quote.prevClose} isChange={false}>
                <Number
                  value={quote.prevClose}
                  precision={metadata.precision}
                  className="price-value"
                />
              </CssFlash>
            </td>
          </tr>
          <tr className="divider">
            <th>出来高</th>
            <td>
              <CssFlash value={quote.volume} isChange={false}>
                <Number
                  value={quote.volume}
                  precision={0}
                  className="price-value"
                />
              </CssFlash>
            </td>
          </tr>
          <tr className="divider">
            <th>売買代金</th>
            <td>
              <CssFlash value={quote.turnover} isChange={false}>
                <Number
                  value={quote.turnover}
                  precision={0}
                  className="price-value"
                />
              </CssFlash>
            </td>
          </tr>
          <tr>
            <th colSpan={2}>52週間値幅</th>
          </tr>
          <tr>
            <td colSpan={2}>
              <div className="range-container">
                <div className="price-range">
                  <div
                    className="current-price-marker"
                    style={{
                      left: `${calcPriceMarkerPosition(
                        close,
                        quote.low52Week,
                        quote.high52Week
                      )}%`
                    }}
                  />
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>
              <Number
                value={quote.low52Week}
                precision={metadata.precision}
                hideIfZero={true}
                className="fifty-two-wk-price"
              />
            </td>
            <td>
              <Number
                value={quote.high52Week}
                precision={metadata.precision}
                hideIfZero={true}
                className="fifty-two-wk-price"
              />
            </td>
          </tr>
          <tr>
            <td>
              <DateComponent value={quote.low52WeekTime} />
            </td>
            <td>
              <DateComponent value={quote.high52WeekTime} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

SymbolQuoteCard.propTypes = {
  symbol: PropTypes.string.isRequired
};

export default SymbolQuoteCard;
